import isEqual from 'lodash-es/isEqual';
import { Observable, of, Subscription, switchMap } from 'rxjs';
import { debounceTime, map, pairwise, startWith, take, takeWhile, tap } from 'rxjs/operators';
import { ListingDatasource } from 'src/app/_shared/components/listing/listing.datasource';
import { KalTableDatasourceParameters } from 'src/app/_shared/modules/kal-table/classes/kal-table-datasource';
import { environment } from 'src/environments/environment';

export abstract class SharedListingDatasource<T> extends ListingDatasource<T> {

    previousConfig: KalTableDatasourceParameters;

    protected subscription?: Subscription;

    protected isFirst = true;

    constructor(protected readonly config: KalTableDatasourceParameters) {
        super(config);

        this.parametersChanges();
    }

    abstract fetchRows(config: KalTableDatasourceParameters): Observable<any>;

    clearParameters$(): Observable<any> {
        return this.refresh$({
            page: {
                number: 0,
                size: this.page.value.size
            },
            sort: null,
            filters: null,
        });
    }

    refresh$(config: KalTableDatasourceParameters = this.parameters): Observable<any> {
        this.subscription?.unsubscribe();

        this.parameters = config;

        return this.fetchRows(this.parameters).pipe(
            take(1),
            takeWhile(() => !this.isClosed()),
            tap(() => {
                this.parametersChanges();
                this.isFirst = false;
            })
        );
    }

    // Permet de lancer un refesh sans bloquer les event type changefilters pendant le loading
    refresh(): void {
        this.subscription?.unsubscribe();
        this.isFirst = true;
        this.parametersChanges();
    }

    setRows(rows: T[]): void {
        const comparator = this.getComparator(this.parameters.sort);

        super.setRows(comparator ? rows.sort(comparator) : rows);
    }

    protected changePage$(config: KalTableDatasourceParameters): Observable<any> {
        return this.fetchRows(config);
    }

    protected changeSort$(config: KalTableDatasourceParameters): Observable<any> {
        this.setRows(this.unfilteredRows);
        return of(null);
    }

    protected changeFilters$(config: KalTableDatasourceParameters): Observable<any> {
        return this.fetchRows(config);
    }

    protected checkParameters$(values: KalTableDatasourceParameters[]): Observable<any> {
        this.previousConfig = values[0];

        const config = values[1];

        if (config.loading) {
            return of(null);
        }

        if (this.isFirst) {
            this.isFirst = false;
            return this.fetchRows(config);
        }

        if (!isEqual(this.previousConfig.filters, config.filters)) {
            return this.changeFilters$(config);
        }

        if (!isEqual(this.previousConfig.page, config.page)) {
            return this.changePage$(config);
        }

        if (!isEqual(this.previousConfig.sort, config.page)) {
            return this.changeSort$(config);
        }

        return of(null);
    }

    private parametersChanges(): void {
        let action$ = this.changesWithoutLoading$.pipe(
            takeWhile(() => !this.isClosed()),
        );

        if (this.isFirst) {
            action$ = action$.pipe(
                startWith(this.parameters)
            );
        }

        this.subscription = action$.pipe(
            map(() => this.parameters),
            pairwise(),
            debounceTime(environment.debounceTime),
            switchMap(values => this.checkParameters$(values))
        ).subscribe();
    }

}
